/**
 * Billing page — per docs/specs/billing.md
 *
 * Revenue + unit-economics KPIs, profit-per-plan, cost breakdown,
 * per-user billing health queue, plan distribution, transactions timeline.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-22-v1
 */
import { Download, ExternalLink } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { KpiTile } from '../../components/kpi-tile';
import { Chip } from '../../components/chip';
import { useToast } from '../../components/toast';
import { useShell } from '../../layout/shell';
import {
    useBillingHealthQueue,
    useBillingKpis,
    usePlanDistribution,
    usePlanProfit,
    useServiceCosts
} from '../../../lib/api';
import type { BillingIssueKind, BillingTransaction } from '../../../lib/types';
import { apiGet, getHttpErrorMessage } from '../../../lib/http';
import { getAccessToken, handleAdminApiAuthError } from '../../../lib/auth';
import { RoleButton } from '../../components/role-gate';
import { BillingHealthDetail } from './billing-health-detail';

const fmtMoney = (n: number) =>
    n < 0
        ? `−$${Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 2 })}`
        : `$${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

const issueTone = (i: BillingIssueKind) => {
    if (i === 'payment_failed' || i === 'dispute' || i === 'multi_failed') return 'danger';
    if (i === 'plan_drift' || i === 'overage') return 'warning';
    if (i === 'card_expiring' || i === 'trial_ending') return 'info';
    return 'muted';
};

const issueLabel = (i: BillingIssueKind) => i.replace(/_/g, ' ');

const txTone = (k: BillingTransaction['kind']) =>
    k === 'success' || k === 'upgrade'
        ? 'success'
        : k === 'failure'
          ? 'danger'
          : k === 'refund' || k === 'downgrade'
            ? 'warning'
            : 'muted';

type BillingPeriod = '7d' | '30d' | '90d' | 'ytd';

function isBillingPeriod(value: string | null): value is BillingPeriod {
    return value === '7d' || value === '30d' || value === '90d' || value === 'ytd';
}

function resolveBillingPeriod(value: string | null): BillingPeriod {
    return isBillingPeriod(value) ? value : '30d';
}

function buildTransactionEndpoint(period: BillingPeriod) {
    return `/api/admin/billing/transactions?period=${encodeURIComponent(period)}`;
}

function buildExportEndpoint(period: BillingPeriod) {
    return `/api/admin/billing/export/pnl?period=${encodeURIComponent(period)}`;
}

function parseFilename(contentDisposition: string | null): string | null {
    if (!contentDisposition) return null;
    const match = /filename="([^"]+)"/.exec(contentDisposition);
    return match?.[1] || null;
}

export function BillingPage() {
    const [params, setParams] = useSearchParams();
    const openId = params.get('health');
    const { setDetailPanel } = useShell();
    const { toast } = useToast();
    const [isExporting, setIsExporting] = useState(false);
    const period = resolveBillingPeriod(params.get('period'));

    const kpis = useBillingKpis();
    const plan = usePlanProfit();
    const costs = useServiceCosts();
    const dist = usePlanDistribution();
    const queue = useBillingHealthQueue();
    const tx = useQuery<BillingTransaction[]>({
        queryKey: ['admin', 'billing', 'transactions', period],
        queryFn: () => apiGet<BillingTransaction[]>(buildTransactionEndpoint(period))
    });
    const reconciliationRows =
        queue.data?.filter(
            (row) =>
                row.issue === 'plan_drift' ||
                row.issue === 'dispute' ||
                row.issue === 'multi_failed'
        ) || [];

    function updateParam(key: string, value: string | null) {
        const next = new URLSearchParams(params);
        if (!value) next.delete(key);
        else next.set(key, value);
        setParams(next, { replace: true });
    }

    function updatePeriod(value: string) {
        updateParam('period', value);
    }

    const handleExport = async () => {
        setIsExporting(true);
        try {
            const token = await getAccessToken();
            const headers = new Headers();
            headers.set('accept', 'text/csv');
            if (token) {
                headers.set('authorization', `Bearer ${token}`);
            }

            const response = await fetch(buildExportEndpoint(period), {
                method: 'GET',
                headers,
                credentials: 'omit'
            });

            if (!response.ok) {
                const text = await response.text();
                let body: unknown = null;
                try {
                    body = text ? JSON.parse(text) : null;
                } catch {
                    body = text;
                }

                const detail =
                    typeof body === 'object' &&
                    body !== null &&
                    'error' in body &&
                    typeof body.error === 'string'
                        ? body.error
                        : undefined;

                if (response.status === 401 || response.status === 403) {
                    handleAdminApiAuthError(response.status as 401 | 403 | 404, detail);
                }
                throw new Error(detail || `Export failed (${response.status})`);
            }

            const blob = await response.blob();
            const fileName =
                parseFilename(response.headers.get('content-disposition')) ||
                `billing-pnl-${period}-${new Date().toISOString().slice(0, 10)}.csv`;

            const href = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = href;
            anchor.download = fileName;
            anchor.click();
            URL.revokeObjectURL(href);
            toast('P&L export downloaded', { detail: fileName });
        } catch (error) {
            const message = getHttpErrorMessage(error, 'P&L export failed');
            toast('P&L export failed', { kind: 'danger', detail: message });
        } finally {
            setIsExporting(false);
        }
    };

    useEffect(() => {
        if (openId) {
            setDetailPanel(
                <BillingHealthDetail rowId={openId} onClose={() => updateParam('health', null)} />
            );
        } else {
            setDetailPanel(null);
        }
        return () => setDetailPanel(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openId, setDetailPanel]);

    return (
        <div className="stack-16">
            <header className="page-header">
                <div className="page-title-group">
                    <h1>Billing</h1>
                    <p>Revenue, unit economics, and the per-user billing health queue.</p>
                </div>
                <div className="page-actions">
                    <select
                        className="select"
                        value={period}
                        aria-label="Period"
                        onChange={(e) => {
                            updatePeriod(e.target.value);
                        }}
                    >
                        <option value="7d">Last 7 days</option>
                        <option value="30d">Last 30 days</option>
                        <option value="90d">Last 90 days</option>
                        <option value="ytd">Year to date</option>
                    </select>
                    <a
                        className="btn btn-ghost"
                        href="https://dashboard.stripe.com"
                        target="_blank"
                        rel="noreferrer"
                    >
                        <ExternalLink size={14} /> Stripe
                    </a>
                    <RoleButton
                        scope="owner_only"
                        className="btn btn-ghost"
                        disabled={isExporting}
                        onClick={handleExport}
                    >
                        <Download size={14} /> {isExporting ? 'Exporting…' : 'Export P&L'}
                    </RoleButton>
                </div>
            </header>

            {kpis.data && (
                <>
                    <section className="kpi-grid kpi-grid-6" aria-label="Revenue metrics">
                        <KpiTile kpi={kpis.data.mrr} />
                        <KpiTile kpi={kpis.data.arr} />
                        <KpiTile kpi={kpis.data.paying} />
                        <KpiTile kpi={kpis.data.churn} />
                        <KpiTile kpi={kpis.data.ltv} />
                        <KpiTile kpi={kpis.data.failed} />
                    </section>
                    <section className="kpi-grid kpi-grid-6" aria-label="Unit economics">
                        <KpiTile kpi={kpis.data.revenue_30d} />
                        <KpiTile kpi={kpis.data.cost_30d} />
                        <KpiTile kpi={kpis.data.profit_30d} />
                        <KpiTile kpi={kpis.data.margin} />
                        <KpiTile kpi={kpis.data.arpu} />
                        <KpiTile kpi={kpis.data.cost_per_free} />
                    </section>
                </>
            )}

            <div className="grid-split">
                <section className="card">
                    <header className="card-header">
                        <h2 className="card-title">Gross profit per plan</h2>
                    </header>
                    <div className="table-wrap">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Plan</th>
                                    <th className="numeric">Users</th>
                                    <th className="numeric">Rev / user</th>
                                    <th className="numeric">Cost / user</th>
                                    <th className="numeric">Profit / user</th>
                                    <th className="numeric">Margin</th>
                                </tr>
                            </thead>
                            <tbody>
                                {plan.data?.map((r) => (
                                    <tr key={r.plan}>
                                        <td>
                                            <Chip
                                                tone={
                                                    r.plan === 'Team' || r.plan === 'Studio'
                                                        ? 'purple'
                                                        : r.plan === 'Pro'
                                                          ? 'info'
                                                          : r.plan === 'Trial'
                                                            ? 'warning'
                                                            : 'muted'
                                                }
                                            >
                                                {r.plan}
                                            </Chip>
                                        </td>
                                        <td className="numeric">{r.users.toLocaleString()}</td>
                                        <td className="numeric">
                                            {r.revenue_per_user === 0
                                                ? '—'
                                                : fmtMoney(r.revenue_per_user)}
                                        </td>
                                        <td className="numeric">{fmtMoney(r.cost_per_user)}</td>
                                        <td
                                            className="numeric"
                                            style={{
                                                color:
                                                    r.profit_per_user > 0
                                                        ? 'var(--success)'
                                                        : r.profit_per_user < 0
                                                          ? 'var(--danger)'
                                                          : undefined
                                            }}
                                        >
                                            {fmtMoney(r.profit_per_user)}
                                        </td>
                                        <td className="numeric text-muted">
                                            {r.margin_pct === null
                                                ? '—'
                                                : `${r.margin_pct.toFixed(1)}%`}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>

                <section className="card">
                    <header className="card-header">
                        <h2 className="card-title">Service cost breakdown</h2>
                    </header>
                    <div className="card-body tight">
                        {costs.data?.map((c) => (
                            <div
                                key={c.provider}
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr auto auto',
                                    gap: 10,
                                    padding: '10px 16px',
                                    borderBottom: '1px solid var(--border)',
                                    fontSize: 12
                                }}
                            >
                                <div>
                                    <div style={{ color: 'var(--text-primary)' }}>{c.provider}</div>
                                    {c.note && (
                                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                                            {c.note}
                                        </div>
                                    )}
                                </div>
                                <span className="mono" style={{ color: 'var(--text-primary)' }}>
                                    {fmtMoney(c.amount)}
                                </span>
                                <span className="text-muted">{c.pct.toFixed(1)}%</span>
                            </div>
                        ))}
                    </div>
                </section>
            </div>

            <section className="card">
                <header className="card-header">
                    <h2 className="card-title">Stripe reconciliation</h2>
                    <Chip tone={reconciliationRows.length ? 'warning' : 'success'}>
                        {reconciliationRows.length ? `${reconciliationRows.length} flags` : 'Clean'}
                    </Chip>
                </header>
                <div className="card-body tight">
                    {reconciliationRows.length ? (
                        reconciliationRows.map((row) => (
                            <div
                                key={row.id}
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr auto auto auto',
                                    gap: 10,
                                    padding: '10px 16px',
                                    borderBottom: '1px solid var(--border)',
                                    fontSize: 12
                                }}
                            >
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ color: 'var(--text-primary)' }}>
                                        {row.user_name}
                                    </div>
                                    <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                                        {row.user_email}
                                    </div>
                                </div>
                                <Chip tone={issueTone(row.issue)}>{issueLabel(row.issue)}</Chip>
                                <span className="text-muted" style={{ minWidth: 220 }}>
                                    {row.detail}
                                </span>
                                <span className="mono">{fmtMoney(row.exposure)}</span>
                            </div>
                        ))
                    ) : (
                        <div className="empty-state">
                            <div className="title">No reconciliation flags</div>
                            <div className="sub">
                                Stripe webhook payloads and local subscription rows are aligned for
                                the current period. Card-expiry alerts are still unavailable because
                                the local subscription cache does not persist payment-method expiry
                                metadata yet.
                            </div>
                        </div>
                    )}
                </div>
            </section>

            <section className="card">
                <header className="card-header">
                    <h2 className="card-title">Per-user billing health</h2>
                    <span className="text-muted" style={{ fontSize: 11 }}>
                        Sorted by exposure
                    </span>
                </header>
                <div className="table-wrap">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>User</th>
                                <th>Plan</th>
                                <th>Issue</th>
                                <th>Detail</th>
                                <th className="numeric">Exposure</th>
                                <th>Detected</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {queue.data?.map((r) => (
                                <tr
                                    key={r.id}
                                    aria-selected={openId === r.id}
                                    onClick={() => updateParam('health', r.id)}
                                >
                                    <td>
                                        <div className="user-meta">
                                            <span className="user-name">{r.user_name}</span>
                                            <span className="user-sub">{r.user_email}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <Chip tone={r.plan === 'pro' ? 'info' : 'purple'}>
                                            {r.plan}
                                        </Chip>
                                    </td>
                                    <td>
                                        <Chip tone={issueTone(r.issue)}>{issueLabel(r.issue)}</Chip>
                                    </td>
                                    <td className="text-muted">{r.detail}</td>
                                    <td className="numeric">{fmtMoney(r.exposure)}</td>
                                    <td className="text-muted">{r.detected}</td>
                                    <td
                                        style={{ textAlign: 'right' }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <button
                                            className="btn btn-sm btn-ghost"
                                            onClick={() => updateParam('health', r.id)}
                                        >
                                            Action
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            <div className="grid-split">
                <section className="card">
                    <header className="card-header">
                        <h2 className="card-title">Plan distribution</h2>
                    </header>
                    <div className="card-body tight">
                        {dist.data?.map((r) => (
                            <div
                                key={r.plan}
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr auto auto auto',
                                    gap: 12,
                                    padding: '10px 16px',
                                    borderBottom: '1px solid var(--border)',
                                    fontSize: 12
                                }}
                            >
                                <span style={{ color: 'var(--text-primary)' }}>{r.plan}</span>
                                <span className="mono">{r.users.toLocaleString()}</span>
                                <span className="text-muted">{r.pct.toFixed(1)}%</span>
                                <span
                                    style={{
                                        color:
                                            r.profit_total > 0
                                                ? 'var(--success)'
                                                : r.profit_total < 0
                                                  ? 'var(--danger)'
                                                  : 'var(--text-muted)'
                                    }}
                                >
                                    {fmtMoney(r.profit_total)}
                                </span>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="card">
                    <header className="card-header">
                        <h2 className="card-title">Recent transactions</h2>
                    </header>
                    <div className="card-body tight">
                        {tx.data?.map((t) => (
                            <div
                                key={t.id}
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '64px auto 1fr auto',
                                    gap: 10,
                                    padding: '10px 16px',
                                    borderBottom: '1px solid var(--border)',
                                    fontSize: 12
                                }}
                            >
                                <span className="mono text-muted">{t.ts}</span>
                                <Chip tone={txTone(t.kind)}>{t.kind}</Chip>
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ color: 'var(--text-primary)' }}>{t.user}</div>
                                    {t.note && (
                                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                                            {t.note}
                                        </div>
                                    )}
                                </div>
                                <span
                                    className="mono"
                                    style={{
                                        color:
                                            t.amount > 0
                                                ? 'var(--text-primary)'
                                                : t.amount < 0
                                                  ? 'var(--danger)'
                                                  : 'var(--text-muted)'
                                    }}
                                >
                                    {t.amount ? fmtMoney(t.amount) : '—'}
                                </span>
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    );
}
